import { MouseEvent, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Image from '@alltrails/core/components/Image';
import DropdownMenu from '@alltrails/denali/components/DropdownMenu';
import IconButton from '@alltrails/denali/components/IconButton';
import IconRenderer from '@alltrails/denali/components/IconRenderer';
import LinkButtonOrDiv from '@alltrails/denali/components/LinkButtonOrDiv';
import Hide from '@alltrails/denali/icons/Hide';
import Overflow from '@alltrails/denali/icons/Overflow';
import { usePOIProfilePhoto } from '@alltrails/explore-service/hooks/usePOIProfilePhoto';
import type { POISearchResult } from '@alltrails/types/AlgoliaResults';
import getPOIIcon from '@alltrails/search/utils/getPOIIcon';
import useHidePOI from '../../../hooks/useHidePOI';
import { useAppDispatch, useAppSelector } from '../../../redux';
import { updateCursor, updateHoveredPOI } from '../../../redux/reducer';
import HidePOIConfirmation from './HidePOIConfirmation';
import styles from './POIPopupCard.module.scss';

type POIPopupCardProps = {
  hasLocationsManagePermission?: boolean;
  href?: string;
  onClick?: (poi: POISearchResult) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onPOICardViewed?: (id: number, index: number) => void;
  poi: POISearchResult;
};

// https://www.figma.com/design/Xmdcv0j315iIELXRMl8UzJ/POIs-Web-%5BHandoff%5D?node-id=483-209809&m=dev
const POIPopupCard = ({ href, onClick, onMouseEnter, onMouseLeave, onPOICardViewed, poi, hasLocationsManagePermission }: POIPopupCardProps) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { ID, location_label, name, photos_count, subtype, trails_count, type_label } = poi;
  const profilePhotoUrl = usePOIProfilePhoto({ poiId: ID, width: 160, height: 180 });
  const hiddenPOIs = useAppSelector(state => state.map.hiddenPOIs);
  const { isHiding, showHideConfirmation, setShowHideConfirmation, onHidePOI } = useHidePOI({ ID, hiddenPOIs });

  const onClickInternal = useCallback(() => {
    dispatch(updateHoveredPOI(undefined));
    dispatch(updateCursor(undefined));
    onClick?.(poi);
  }, [dispatch, onClick, poi]);

  const handleContextMenu = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    setIsDropdownOpen(true);
  };

  useEffect(() => {
    onPOICardViewed?.(ID, 0);
  }, [onPOICardViewed, ID]);

  const actionElement = hasLocationsManagePermission ? (
    <div className={styles.adminActions}>
      <DropdownMenu
        anchor={
          <IconButton
            icon={{ Component: Overflow }}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setIsDropdownOpen(!isDropdownOpen);
            }}
            title={intl.formatMessage({ defaultMessage: 'Admin actions' })}
            testId="admin-actions-button"
            variant="transparent"
            size="sm"
          />
        }
        isOpen={isDropdownOpen}
        onCloseRequest={() => setIsDropdownOpen(false)}
        options={[
          {
            testId: 'hide',
            title: hiddenPOIs?.includes(ID) ? <FormattedMessage defaultMessage="Hidden" /> : <FormattedMessage defaultMessage="Hide" />,
            onClick: (e?: React.MouseEvent<HTMLElement>) => {
              e?.preventDefault();
              e?.stopPropagation();
              setShowHideConfirmation(true);
            },
            leadingIcon: { icon: { Component: Hide } }
          }
        ]}
      />
    </div>
  ) : null;

  return (
    <LinkButtonOrDiv
      className={styles.container}
      linkInfo={href ? { href, prefetch: true } : undefined}
      onClick={e => {
        if (e?.defaultPrevented) return;
        onClickInternal();
      }}
      onMouseEnter={() => onMouseEnter?.()}
      onMouseLeave={onMouseLeave}
      onContextMenu={handleContextMenu}
      testId={`poi-popup-card-${ID}`}
    >
      <div className={styles.imageContainer}>
        {!!photos_count ? (
          <Image alt={name} className={styles.image} fill src={profilePhotoUrl} />
        ) : (
          <div className={styles.iconFallback}>
            <IconRenderer icon={{ Component: getPOIIcon(subtype, true /* filled */), color: '--color-neutral-white' }} defaults={{}} />
          </div>
        )}
      </div>
      <div className={styles.info}>
        <div className={styles.name}>{name}</div>
        <div className={styles.area}>{location_label}</div>
        <div className={styles.details}>
          <FormattedMessage defaultMessage="{trailCount, plural, one {# trail} other {# trails}}" values={{ trailCount: trails_count }} /> •{' '}
          {type_label}
        </div>
      </div>
      {actionElement}
      {showHideConfirmation && (
        <HidePOIConfirmation onHide={onHidePOI} setShowHideConfirmation={setShowHideConfirmation} isHiding={isHiding} popupCardType="poi" />
      )}
    </LinkButtonOrDiv>
  );
};

export default POIPopupCard;
